import { ReactNode, useCallback, useMemo } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ClientLogClient,
  ClientLogRequest,
  LogLevel,
  PosClientConfig,
} from 'src/WebApiController';

type RootErrorBoundaryProps = {
  children: ReactNode;
  webClientConfig: PosClientConfig;
  fallback?: React.ComponentType<FallbackProps>;
};

/**
 * Root-level error boundary that catches unhandled React errors
 * and logs them to the server using ClientLogClient.
 *
 * This should be placed high in the component tree (under AppContextProvider)
 * to catch errors from the entire application.
 *
 * Unlike withErrorBoundary/LocalErrorBoundary, this component doesn't use
 * useClientLogContext() because ClientLogContextProvider is not available
 * at the root level. Instead, it creates its own ClientLogClient instance.
 */
export const RootErrorBoundary = ({
  children,
  webClientConfig,
  fallback,
}: RootErrorBoundaryProps) => {
  const logClient = useMemo(
    () => new ClientLogClient(webClientConfig),
    [webClientConfig]
  );

  const FallbackComponent = fallback || ((_: FallbackProps) => <PosSpinner />);

  const onError = useCallback(
    (error: Error, info: { componentStack?: string | null }) => {
      console.error('Root ErrorBoundary caught an error:', error, info);

      const logRequest: ClientLogRequest = {
        logLevel: LogLevel.Error,
        applicationSource: REACT_APP_NAME,
        errorId: 'RootErrorBoundary',
        error: error.message || 'Unknown error',
        pageUrl: window.location.href,
        componentStack: info.componentStack || null,
        timestamp: new Date().toISOString(),
        additionalInfo: {
          errorName: error.name,
          errorStack: error.stack,
        },
      };

      tryInvokeApi(
        async () => {
          await logClient.logEvents([logRequest]);
        },
        (logError) => {
          console.error('Failed to log error to server:', logError);
        }
      );
    },
    [logClient]
  );

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
      {children}
    </ErrorBoundary>
  );
};
